import styled from '@emotion/styled';
import { layout, palette, radius, spacing, typography } from '../../../style-utils/theme';
import { down } from '../../../style-utils/media-queries';
import { FullWidthButton, GhostButton, SecondaryButton } from '../onboarding.styled';

/**
 * THE STAY GUEST'S PAGES — styles (plan S3). Reuse only: the onboarding kit
 * (Shell, Card, Heading, Field, Input …) carries the look; this file adds the
 * few arrangements the check-in needs, on the same tokens.
 *
 * PHONE FIRST (WebKit, 320px): every button may wrap onto two lines rather than
 * push the card sideways, so the kit's fixed 44px height becomes a 44px FLOOR
 * here; the last action of a view sits above var(--safe-bottom).
 */

const wrapLabel = `
  height: auto;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  white-space: normal;
  text-align: center;
  overflow-wrap: anywhere;
`;

export const Primary = styled(FullWidthButton)`
  ${wrapLabel};
  margin-top: 0;
`;

export const Secondary = styled(SecondaryButton)`
  ${wrapLabel};
  width: 100%;
  text-decoration: none;
`;

export const Ghost = styled(GhostButton)`
  ${wrapLabel};
  width: 100%;
  text-decoration: none;
`;

/** The same two looks on a link (WhatsApp, "Open on this phone"). */
export const SecondaryLink = Secondary.withComponent('a');
export const GhostLink = Ghost.withComponent('a');

/** The actions that close a view: a column, clear of the phone's home bar. */
export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin-top: ${spacing.sm};
  padding-bottom: var(--safe-bottom);
`;

export const Facts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.md};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const Fact = styled.p`
  margin: 0;
`;

export const Quiet = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/** A server-written block of text (the consent words): its line breaks kept. */
export const Prose = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.relaxed};
  color: ${palette.ink};
  white-space: pre-line;
  overflow-wrap: anywhere;
`;

export const Body = styled.p`
  margin: ${spacing.xs} 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

/** The step heading under the greeting of the one-adult page. */
export const SubTitle = styled.h2`
  margin: ${spacing.sm} 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const CheckRow = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.sm};
  align-items: center;
  min-height: ${layout.touchTarget};
  margin-top: ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.ink};
  cursor: pointer;

  input {
    width: ${spacing.md};
    height: ${spacing.md};
    margin: 0;
    accent-color: ${palette.teal};
  }
`;

export const StepperRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.sm};
`;

export const StepperLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.inkSoft};
`;

/* ── the party ── */

export const PartyList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.md};
  padding: 0;
  list-style: none;
`;

export const PartyItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

export const PartyHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const PartyName = styled.span`
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const PartyActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const AddBox = styled.form`
  display: flex;
  flex-direction: column;
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};

  ${down('sm')} {
    padding: ${spacing.sm} ${spacing.xs};
  }
`;

export const LinkBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding-top: ${spacing.xs};
`;
